import { AnyAsset, CalculosInventario, Inventario } from 'src/drheranca/types';
import { getAnyAssetValueForPurpose } from 'src/drheranca/services';

export type AcordoMatrixBase = 'escritura' | 'partilha';

export type AcordoValuationPurpose = 'DATA_ESCRITURA' | 'DATA_PARTILHA';

export interface AcordoMatrixRow {
  bem: string;
  valorTotal: number;
  valorConjuge: number;
  valorHerdeiro: number;
  fracaoNota?: string;
}

type AcordoUsufrutoData = NonNullable<CalculosInventario['acordoUsufruto']>;

function findAcordoAssetById(
  inventario: Inventario | undefined,
  id: number | string | null | undefined,
  category: 'imoveis' | 'veiculos' | 'investimentos',
): AnyAsset | undefined {
  if (!inventario || id == null) return undefined;
  const list =
    ((inventario as unknown as Record<string, AnyAsset[] | undefined>)[category]) || [];
  return list.find((a) => String(a?.id) === String(id));
}

function baseToPurpose(base: AcordoMatrixBase): AcordoValuationPurpose {
  return base === 'escritura' ? 'DATA_ESCRITURA' : 'DATA_PARTILHA';
}

/** Linhas da matriz bem × pessoa (UI e Excel). */
export function buildAcordoMatrixRows(
  acordo: AcordoUsufrutoData,
  inventario: Inventario | undefined,
  base: AcordoMatrixBase,
): AcordoMatrixRow[] {
  const purpose = baseToPurpose(base);
  const rows: AcordoMatrixRow[] = [];

  const imovel = findAcordoAssetById(inventario, acordo.imovelId, 'imoveis');
  if (imovel) {
    const total = getAnyAssetValueForPurpose(imovel, purpose);
    rows.push({
      bem: (imovel as { descricao?: string }).descricao || 'Imóvel',
      valorTotal: total,
      valorConjuge: total * acordo.fracaoUsufrutoImovel,
      valorHerdeiro: total * acordo.fracaoNuaPropriedadeImovel,
      fracaoNota: `Usufruto ${(acordo.fracaoUsufrutoImovel * 100).toFixed(2)}% / Nua-propriedade ${(acordo.fracaoNuaPropriedadeImovel * 100).toFixed(2)}%`,
    });
  }

  const veiculo = findAcordoAssetById(inventario, acordo.veiculoId, 'veiculos');
  if (veiculo) {
    const total = getAnyAssetValueForPurpose(veiculo, purpose);
    rows.push({
      bem: (veiculo as { descricao?: string }).descricao || 'Veículo',
      valorTotal: total,
      valorConjuge: total,
      valorHerdeiro: 0,
      fracaoNota: '100% ao cônjuge (pagamento integral em meação)',
    });
  }

  const investimento = findAcordoAssetById(
    inventario,
    acordo.investimentoId,
    'investimentos',
  );
  if (investimento) {
    const total = getAnyAssetValueForPurpose(investimento, purpose);
    rows.push({
      bem: (investimento as { descricao?: string }).descricao || 'Saldo bancário',
      valorTotal: total,
      valorConjuge: total,
      valorHerdeiro: 0,
      fracaoNota: '100% ao cônjuge (pagamento integral em meação)',
    });
  }

  return rows;
}

export function resolveAcordoPartilhaNames(calculos: CalculosInventario) {
  const partilhas = calculos.partilhaPorHerdeiro || [];
  const conjugePart = partilhas.find((p) => (p.valorMeacao ?? 0) > 0.01);
  const herdeiroPart = partilhas.find(
    (p) => p !== conjugePart && (p.valorRecebidoBruto ?? 0) > 0.01,
  );
  return {
    conjugeNome: conjugePart?.herdeiroNome || 'Cônjuge meeiro',
    herdeiroNome: herdeiroPart?.herdeiroNome || 'Herdeiro',
  };
}
